"use client";

import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";

import RichText from "@/components/atoms/rich-text/rich-text";
import withBrand from "@/components/with-brand";
import { SingleTrip } from "@/config/app-constant";
import { usePromoBanner } from "@/hooks/use-promo-banner";
import { RootState } from "@/store/root-reducer";
import { PromoBannerContentType } from "@/utils/schema/quote-page-schema.utils";

import ApplyButton from "./apply-button";
import TermsLink from "./terms-link";

interface PromoCodeBannerWithTitleProps {
  content: PromoBannerContentType;
  className?: string;
  brand?: string;
}

// Avanti brand configuration
const AVANTI_CONFIG = {
  bgColor: "#FFD873",
  bgGradientDesktop: undefined,
  bgGradientMobile: undefined,
  textColor: "#005A5E",
  promoCodeColor: "#E2005D",
  buttonColor: "#005A5E",
  iconStroke: "#005A5E",
  fontWeights: {
    main: "font-[700]",
    description: "font-[700]",
    useCode: "font-[700]",
    button: "font-[700]",
    terms: "font-[600]",
    mobileGet: "font-[600]",
    mobileDescription: "font-[600]",
    title: "font-[700]",
  },
  lineHeight: "leading-[24px]",
  titleLineHeight: "leading-[24px]",
  title: {
    color: "#005A5E",
    fontSizeDesktop: "24px",
    fontSizeMobile: "24px",
  },
};

// Staysure brand configuration
const STAYSURE_CONFIG = {
  bgColor: undefined,
  bgGradientDesktop: "linear-gradient(90.32deg, #880F27 2.69%, #8F3F96 57.47%)",
  bgGradientMobile: "linear-gradient(130.38deg, #880F27 3.29%, #8F3F96 73.34%)",
  textColor: undefined,
  promoCodeColor: "#F6B052",
  buttonColor: "#E8F7E9",
  iconStroke: "white",
  fontWeights: {
    main: "font-[500]",
    description: "font-[600]",
    useCode: "font-[600]",
    button: "font-[700]",
    terms: "font-[600]",
    mobileGet: "font-[400] leading-[20px]",
    mobileDescription: "font-[400]",
    title: "font-[400]",
  },
  lineHeight: "leading-[22px]",
  titleLineHeight: "leading-[28px]",
  title: {
    color: "#FFFFFF",
    fontSizeDesktop: "25px",
    fontSizeMobile: "20px",
  },
};

// Brand configuration selector
const getBrandConfig = (isAvanti: boolean) =>
  isAvanti ? AVANTI_CONFIG : STAYSURE_CONFIG;

// Hex color validation utility
const isValidHexColor = (color: string | undefined): boolean => {
  if (!color) return false;
  const hexRegex = /^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/;
  return hexRegex.test(color);
};

function PromoCodeBannerWithTitle({
  content,
  className = "",
  brand = "staysure",
}: Readonly<PromoCodeBannerWithTitleProps>) {
  // Get trip type from Redux to determine which content to show
  const tripDetails = useSelector(
    (state: RootState) => state.quote.attributes.tripDetails
  );
  const isSingleTrip = tripDetails?.policyType === SingleTrip;

  // Brand-specific configuration
  const isAvanti = brand === "avanti";
  const brandConfig = getBrandConfig(isAvanti);

  // Extract appropriate content based on trip type
  const title = isSingleTrip
    ? content.singleTripTitle || ""
    : content.multiTripTitle || "";

  const titleColorFromCMS = isSingleTrip
    ? content.singleTripTitleColor
    : content.multiTripTitleColor;

  // Validate and apply title color with fallback to brand default
  const titleColor = isValidHexColor(titleColorFromCMS)
    ? titleColorFromCMS
    : brandConfig.title.color;

  // Fix double hash color issue from Magnolia rich text editor
  const description = isSingleTrip
    ? content.singleTripContent || ""
    : content.multiTripContent || "";

  const promoDescription = isSingleTrip
    ? content.singleTripPromoDescription || ""
    : content.multiTripPromoDescription || "";

  const promoCode = isSingleTrip
    ? content.singleTripPromoCode || ""
    : content.multiTripPromoCode || "";

  const termsText = isSingleTrip
    ? content.singleTripTermsText || ""
    : content.multiTripTermsText || "";

  const termsUrl = isSingleTrip
    ? content.singleTripTermsUrl || ""
    : content.multiTripTermsUrl || "";

  const applyButtonLabel = isSingleTrip
    ? content.singleTripApplyButtonLabel || ""
    : content.multiTripApplyButtonLabel || "";

  const appliedButtonLabel = isSingleTrip
    ? content.singleTripAppliedButtonLabel || ""
    : content.multiTripAppliedButtonLabel || "";

  const bannerStatus = content.bannerStatus;

  // Visibility controls - default to false if not specified
  const showBanner = isSingleTrip
    ? content.singleTripShowBanner ?? false
    : content.multiTripShowBanner ?? false;

  const showButton = isSingleTrip
    ? content.singleTripShowButton ?? false
    : content.multiTripShowButton ?? false;

  const showTerms = isSingleTrip
    ? content.singleTripShowTerms ?? false
    : content.multiTripShowTerms ?? false;

  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] =
    useState<React.JSX.Element | null>(null);
  const previousPromoCodeRef = React.useRef<string | null>(null);
  const hasInitialized = React.useRef(false);

  const { isPromoBannerActive, applyPromoCode, isButtonDisabled, newQuote } =
    usePromoBanner(promoCode, bannerStatus);

  const appliedPromoCode = useSelector(
    (state: RootState) =>
      state.quote.attributes?.paymentDetails?.discounts?.promoCode
  );

  // Capture the initial promo code from Redux when component mounts (only once)
  useEffect(() => {
    if (
      !hasInitialized.current &&
      appliedPromoCode?.valid &&
      appliedPromoCode?.code
    ) {
      previousPromoCodeRef.current = appliedPromoCode.code.toLowerCase();
      hasInitialized.current = true;
    }
  }, [appliedPromoCode]);

  // Check for promo code validation errors and success after quote update
  useEffect(() => {
    if (newQuote) {
      const appliedCode =
        newQuote?.attributes?.paymentDetails?.discounts?.promoCode;

      if (appliedCode) {
        if (!appliedCode.valid && appliedCode.message) {
          setErrorMessage(
            "The promo code couldn't be applied at the moment. Please try again, or come back in a moment."
          );
          setSuccessMessage(null);
        } else if (appliedCode.valid && appliedCode.code) {
          setErrorMessage(null);

          // Check if this is a code switch (previous code exists and is different from current)
          const currentCode = appliedCode.code.toLowerCase();
          const previousCode = previousPromoCodeRef.current;

          if (previousCode && previousCode !== currentCode) {
            setSuccessMessage(
              <>
                Promo code <span className="font-bold">{appliedCode.code}</span>{" "}
                has been applied. Previous promo code removed.
              </>
            );
          } else {
            setSuccessMessage(null);
          }
        }
      }
    }
  }, [newQuote]);

  // Check if this banner's promo code is already applied
  const isPromoAlreadyApplied = useMemo(() => {
    return (
      appliedPromoCode?.valid &&
      appliedPromoCode?.code?.toLowerCase() === promoCode?.toLowerCase()
    );
  }, [appliedPromoCode, promoCode]);

  // Wrapper function to apply promo code
  const handleApplyPromoCode = async () => {
    await applyPromoCode();
  };

  // Early returns for visibility checks
  if (!isPromoBannerActive) return null;
  if (!showBanner) return null;

  return (
    <div
      className={`col-span-12 mx-spacing-l md:mx-spacing-xxl mt-spacing-m lg:my-spacing-xl lg:mb-spacing-xs`}
    >
      {/* Desktop View (xl and up) */}
      <header
        className={`hidden xl:flex relative rounded-[16px] py-spacing-s text-white shadow-md mx-auto ${className}`}
        style={{
          background: brandConfig.bgColor || brandConfig.bgGradientDesktop,
          maxWidth: "1104px",
        }}
        aria-label="Promotional offer"
      >
        <div className="flex flex-col w-full gap-spacing-xxs">
          {/* Title */}
          <div className="w-full px-spacing-xl text-center">
            <h2
              className={`${brandConfig.fontWeights.title} ${brandConfig.titleLineHeight} font-header-font`}
              style={{
                color: titleColor,
                fontSize: brandConfig.title.fontSizeDesktop,
              }}
            >
              {title}
            </h2>
          </div>
          {/* Content wrapper with flex-wrap for responsive layout */}
          <div className="flex items-center justify-center flex-wrap gap-x-spacing-m gap-y-spacing-xs w-full px-spacing-xl">
            {/* Tag Icon and Get text grouped */}
            <div className="flex items-center gap-[12px]">
              {/* Rich text content (description + discount combined) */}
              <div
                className={`${brandConfig.fontWeights.main} text-[20px] leading-[27px] [&_strong]:font-[600]`}
                style={
                  brandConfig.textColor
                    ? { color: brandConfig.textColor }
                    : undefined
                }
              >
                <RichText
                  richText={description}
                  className="inline"
                  preserveFontSize={true}
                />
              </div>
            </div>

            {/* Promo Description - conditionally rendered */}
            {promoDescription && (
              <div
                className={`font-[700] text-[18px] leading-[27px] [&_*]:font-[700]`}
                style={
                  brandConfig.textColor
                    ? { color: brandConfig.textColor }
                    : undefined
                }
              >
                <RichText
                  richText={promoDescription}
                  className="inline"
                  preserveFontSize={true}
                />
              </div>
            )}

            {/* Apply Button - conditionally rendered */}
            {showButton && (
              <ApplyButton
                textSize="text-[18px]"
                iconSize={isPromoAlreadyApplied ? 24 : 19.5}
                className="flex-shrink-0"
                isPromoAlreadyApplied={isPromoAlreadyApplied}
                applyButtonLabel={applyButtonLabel}
                appliedButtonLabel={appliedButtonLabel}
                isButtonDisabled={isButtonDisabled}
                promoCode={promoCode}
                onApplyPromoCode={handleApplyPromoCode}
                style={{ color: brandConfig.buttonColor }}
                fontWeight={brandConfig.fontWeights.button}
              />
            )}

            {/* Terms Link - conditionally rendered */}
            {showTerms && termsText && termsUrl && (
              <TermsLink
                termsText={termsText}
                termsUrl={termsUrl}
                className="whitespace-nowrap"
                style={
                  brandConfig.textColor
                    ? { color: brandConfig.promoCodeColor }
                    : undefined
                }
                fontWeight={brandConfig.fontWeights.terms}
              />
            )}
          </div>
        </div>
      </header>

      {/* Mobile/Tablet View */}
      <header
        className={`xl:hidden relative rounded-[16px] px-spacing-m py-spacing-s text-white shadow-md ${className}`}
        style={{
          background: brandConfig.bgColor || brandConfig.bgGradientMobile,
        }}
        aria-label="Promotional offer"
      >
        {/* Content stacked vertically */}
        <div className="flex flex-col gap-[4px]">
          {/* Title */}
          <div className="w-full text-center mb-[4px]">
            <h2
              className={`${brandConfig.fontWeights.title} font-header-font text-[20px] md:text-[25px] leading-[21px] md:leading-[28px]`}
              style={{
                color: titleColor,
              }}
            >
              {title}
            </h2>
          </div>
          {/* Rich text content */}
          <div
            className={`text-[16px] lg:text-[20px] lg:${brandConfig.lineHeight} text-center`}
          >
            <div
              className={`${brandConfig.fontWeights.mobileGet} [&_strong]:font-[600] [&_strong]:leading-[20px]`}
              style={
                brandConfig.textColor
                  ? { color: brandConfig.textColor }
                  : undefined
              }
            >
              <RichText richText={description} className="inline" />
            </div>
          </div>

          {/* Bottom section - Responsive layout for Promo Description, Button, and Terms */}
          {/* Mobile (<md): Column - Description row 1, Button+Terms row 2 */}
          {/* Tablet (≥md): Row - Description, Button, Terms all in one row */}
          <div className="flex flex-col md:flex-row items-center justify-center gap-spacing-s mt-[2px]">
            {/* Promo Description - conditionally rendered */}
            {promoDescription && (
              <div
                className={`font-[700] text-[14px] lg:text-[18px] leading-[21px] lg:leading-[27px] text-center md:text-left [&_*]:font-[700]`}
                style={
                  brandConfig.textColor
                    ? { color: brandConfig.textColor }
                    : undefined
                }
              >
                <RichText richText={promoDescription} className="inline" />
              </div>
            )}

            {/* Button and Terms wrapper - stays together on mobile */}
            <div className="flex items-center justify-center gap-spacing-s">
              {/* Apply Button - conditionally rendered */}
              {showButton && (
                <ApplyButton
                  textSize="text-[16px] leading-[21px] md:leading-[24px] lg:text-[18px]"
                  iconSize={isPromoAlreadyApplied ? 24 : 15.75}
                  isPromoAlreadyApplied={isPromoAlreadyApplied}
                  applyButtonLabel={applyButtonLabel}
                  appliedButtonLabel={appliedButtonLabel}
                  isButtonDisabled={isButtonDisabled}
                  promoCode={promoCode}
                  onApplyPromoCode={handleApplyPromoCode}
                  style={{ color: brandConfig.buttonColor }}
                  fontWeight={brandConfig.fontWeights.button}
                />
              )}

              {/* Terms Link - conditionally rendered */}
              {showTerms && termsText && termsUrl && (
                <TermsLink
                  termsText={termsText}
                  termsUrl={termsUrl}
                  style={{ color: brandConfig.promoCodeColor }}
                  fontWeight={brandConfig.fontWeights.terms}
                />
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Error Message - Below banner for both views */}
      {errorMessage && (
        <div
          className="text-center text-[16px] leading-[24px] font-[400] font-body-font text-text-critical mt-[10px]"
          role="alert"
        >
          {errorMessage}
        </div>
      )}

      {/* Success Message - Below banner for both views */}
      {successMessage && (
        <div
          className="text-center text-[16px] leading-[24px] font-[400] font-body-font text-text-success mt-[10px] text-text-positive"
          role="alert"
        >
          {successMessage}
        </div>
      )}
    </div>
  );
}

export default withBrand(PromoCodeBannerWithTitle);
